// ./src/pages/private/EventGuestsPage/guestScope.ts
//
// Scope config that adapts the shared guest manager to two pages:
//   • Guests — attendees (role = guest). Role facet hidden; remove = delete.
//   • Crew   — everyone who runs the event (co-hosts, staff, volunteers, speakers,
//              DJs/performers, vendors, …). Role shown; "remove" DEMOTES to guest
//              (they slide over to the Guests list) rather than deleting.
// Kept in its own module so the page module only exports React components
// (lazyNamed types a route module as all-components).

import { CREW_PAGE_ROLES, EVENT_GUEST_ROLE, EVENT_GUEST_ROLE_LABELS } from "@/utils/api";

/** How the "remove" action behaves on this page. */
export type RemoveMode = "delete" | "demote";

/** User-facing copy that differs between Guests and Crew. */
export interface GuestScopeLabels {
  title: string;
  add: string;
  bulkAdd: string;
  emptyTitle: string;
  emptyDesc: string;
  searchPh: string;
  /** Label for the remove affordance (row / bulk / confirm button). */
  removeActionLabel: string;
  removeTitle: string;
  removeMessage: string;
  bulkRemoveTitle: string;
  /** May contain {{count}}. */
  bulkRemoveMessage: string;
}

export interface GuestScope {
  key: "guests" | "team" | "arrivals";
  /** URL segment under the event workspace (`/events/:eventId/<segment>`) — drives the add/edit routes. */
  routeSegment: "guests" | "crew" | "arrivals";
  /** Roles this page lists (empty = don't filter by role — use attendeeOnly instead). */
  roleFilter: string[];
  /** List only counted attendees (is_attendee=true), regardless of role — the Guests page. */
  attendeeOnly?: boolean;
  /** Default role for a new row. */
  defaultRole: string;
  /** Role choices offered in the drawer / bulk edit. */
  roleOptions: { value: string; label: string }[];
  /** Whether role is a meaningful facet here (hidden on Guests — everyone's a guest). */
  showRole: boolean;
  /** Offer a "Buyers only" filter (ticket buyers who aren't counted attendees). Guests page only. */
  showBuyersFilter?: boolean;
  /** delete = remove from the event; demote = set role to guest (moves to Guests). */
  removeMode: RemoveMode;
  labels: GuestScopeLabels;
}

const CREW_ROLE_OPTIONS = CREW_PAGE_ROLES.map((value) => ({ value, label: EVENT_GUEST_ROLE_LABELS[value] }));

export const GUESTS_SCOPE: GuestScope = {
  key: "guests",
  routeSegment: "guests",
  // The Guests page lists every counted attendee (is_attendee=true) — this includes
  // a crew member who also attends — so it filters on attendee, not role.
  roleFilter: [],
  attendeeOnly: true,
  defaultRole: EVENT_GUEST_ROLE.GUEST,
  roleOptions: [{ value: EVENT_GUEST_ROLE.GUEST, label: EVENT_GUEST_ROLE_LABELS[EVENT_GUEST_ROLE.GUEST] }],
  showRole: false,
  showBuyersFilter: true,
  removeMode: "delete",
  labels: {
    title: "Guests",
    add: "Add guest",
    bulkAdd: "Add from contacts",
    emptyTitle: "No guests yet",
    emptyDesc: "Add guests from your contacts or type someone in.",
    searchPh: "Search name, email, phone, code…",
    removeActionLabel: "Remove",
    removeTitle: "Remove this guest?",
    // Copy softened because a crew member removed here stays as crew (just off the
    // guest list); a plain guest is deleted from the event.
    // #187 — a removed guest takes their tickets with them (cancelled, the places back on sale); paid ones are asked about.
    removeMessage: "They’ll be removed from the guest list, and any tickets they hold are cancelled. If they’re also crew they stay on the Crew page.",
    bulkRemoveTitle: "Remove selected guests?",
    bulkRemoveMessage: "This removes {{count}} selected guest(s) from the guest list, and cancels any tickets they hold.",
  },
};

/** Crew scope — the Crew page, built on the same manager. */
export const TEAM_SCOPE: GuestScope = {
  key: "team",
  routeSegment: "crew",
  roleFilter: CREW_PAGE_ROLES,
  defaultRole: EVENT_GUEST_ROLE.STAFF,
  roleOptions: CREW_ROLE_OPTIONS,
  showRole: true,
  removeMode: "demote",
  labels: {
    title: "Crew",
    add: "Add crew member",
    bulkAdd: "Add from contacts",
    emptyTitle: "No crew yet",
    emptyDesc: "Add co-hosts, staff, volunteers, speakers, DJs and other helpers who make the event happen.",
    searchPh: "Search crew…",
    removeActionLabel: "Remove from crew",
    removeTitle: "Remove from crew?",
    removeMessage: "They’ll become a regular guest — still on the event, just no longer crew.",
    bulkRemoveTitle: "Remove selected from crew?",
    bulkRemoveMessage: "{{count}} member(s) will become regular guests.",
  },
};

/**
 * ROADMAP #177 — the Arrivals page (owner, 2026-10-17: the guest list was overwhelmed — "a new page … focused on arrivals
 * and such … clear what it shows, user can easily read all the information, it is fast and convenient to use"). The same
 * manager reading the same attendees, opened on "Arrived", latest arrival first; it reads and opens a guest's drawers
 * (edit, answers, notes, BIB) but adds and removes nobody.
 */
export const ARRIVALS_SCOPE: GuestScope = {
  key: "arrivals",
  routeSegment: "arrivals",
  // ⓘ Not `attendeeOnly`: the page reads `atTheDoor` (#286) — the attendees, and the buyers of a headcount order.
  roleFilter: [],
  defaultRole: EVENT_GUEST_ROLE.GUEST,
  roleOptions: [{ value: EVENT_GUEST_ROLE.GUEST, label: EVENT_GUEST_ROLE_LABELS[EVENT_GUEST_ROLE.GUEST] }],
  showRole: false,
  removeMode: "delete",
  labels: {
    title: "Arrivals",
    add: "Add guest",
    bulkAdd: "Add from contacts",
    emptyTitle: "Nobody has arrived yet",
    emptyDesc: "Guests appear here as they are checked in at the door.",
    searchPh: "Search name, email, phone, code…",
    removeActionLabel: "Remove",
    removeTitle: "Remove this guest?",
    removeMessage: "They’ll be removed from the guest list, and any tickets they hold are cancelled.",
    bulkRemoveTitle: "Remove selected guests?",
    bulkRemoveMessage: "This removes {{count}} selected guest(s) from the guest list, and cancels any tickets they hold.",
  },
};

/** A badge color for a guest role (crew roles read as violet/indigo/etc). */
export const roleBadgeColor = (role?: string | null): string => {
  switch (role) {
    case EVENT_GUEST_ROLE.HOST:
    case EVENT_GUEST_ROLE.CO_HOST:
    case EVENT_GUEST_ROLE.ORGANIZER:
      return "violet";
    case EVENT_GUEST_ROLE.STAFF:
    case EVENT_GUEST_ROLE.VOLUNTEER:
      return "indigo";
    case EVENT_GUEST_ROLE.SPEAKER:
    case EVENT_GUEST_ROLE.PERFORMER:
      return "grape";
    case EVENT_GUEST_ROLE.VENDOR:
      return "teal";
    case EVENT_GUEST_ROLE.GUEST:
      return "gray";
    default:
      return "blue";
  }
};

/**
 * Owner, 2026-10-23 — a TICKETED event in HEADCOUNT mode has no guests as such: everyone on it bought tickets, and holds
 * places as a number on their order, not a name each (role `buyer`, not a counted attendee). The Guests page used to open
 * on attendees — an EMPTY list — and the host had to find the List switch and pick Buyers. There it lists the buyers from
 * the start, and drops the RSVP status and List facets (nobody RSVPs; there is nothing to switch to).
 * ⚠ It lists the buyers AND anyone added by hand (role `guest`): a ticketed headcount event can still have a guest typed
 *   in (one live event does), and with the switch gone a buyers-only list would leave them nowhere on this page.
 *   Crew stay on the Crew page.
 */
export const listsBuyersAndGuests = (
  scope: Pick<GuestScope, "key">,
  event: { eventMode?: string | null; guestTracking?: string | null } | null | undefined,
): boolean => scope.key === "guests" && event?.eventMode === "ticketed" && event?.guestTracking === "headcount";

/**
 * A host asked (2026-11-03): six purchasers on the list, nine tickets on the dashboard, and nothing said who bought how many.
 * Each row of a ticketed event's Guests list says how many tickets it stands for. The Arrivals list has its own "2 of 2 in".
 */
export const showsGuestTickets = (
  scope: Pick<GuestScope, "key">,
  event: { eventMode?: string | null } | null | undefined,
): boolean => scope.key === "guests" && event?.eventMode === "ticketed";

/** The roles that list reads — every role that holds places through this page on such an event. */
export const BUYERS_AND_GUESTS_ROLES: string[] = [EVENT_GUEST_ROLE.BUYER, EVENT_GUEST_ROLE.GUEST];
